import React, { useEffect, useState } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faPlus,
    faEdit,
    faTrash,
    faTimes,
    faSearch
} from "@fortawesome/free-solid-svg-icons";

import Breadcrumb from "../../components/Ui_Element/Breadcrumb";
import DotLoader from "../../components/Ui_Element/DotLoader";
import { post, getlist, deleteitem } from "../../contexts/apicontexts";
import config from "../../config";

const PAGE_SIZE = 10;

export default function Customer() {
    const toDateInputValue = (date) => {
        const offsetMs = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - offsetMs).toISOString().split("T")[0];
    };

    const getDefaultStartDate = () => toDateInputValue(new Date());
    const getDefaultEndDate = () => {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        return toDateInputValue(date);
    };

    const [customers, setCustomers] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const [isLoder, setIsLoder] = useState(false);
    const [errors, setErrors] = useState({});

    const [formState, setFormState] = useState({
        id: 0,
        name: "",
        customerCode: "",
        address: "",
        email: "",
        mobile: "",
        whatsApp: "",
        domain: "",
        agreementStartDate: getDefaultStartDate(),
        agreementEndDate: getDefaultEndDate(),
        isCurrentCustomer: true,
        description: "",
        organizationId: 0,
        empId: 0,
        empUserId: ""
    });

    const validateForm = () => {
        const newErrors = {};

        if (!formState.name?.trim()) newErrors.name = "Name is required";
        if (!formState.customerCode?.trim()) {
            newErrors.customerCode = "Customer Code is required";
        } else if (!/^\d{6,}$/.test(formState.customerCode.trim())) {
            newErrors.customerCode = "Customer Code must be at least 6 digits";
        }
        if (!formState.email?.trim()) {
            newErrors.email = "Email is required";
        } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
            newErrors.email = "Email is invalid";
        }
        if (!formState.mobile?.trim()) newErrors.mobile = "Mobile is required";
        if (!formState.organizationId || formState.organizationId === 0) {
            newErrors.organizationId = "Organization is required";
        }
        if (!formState.agreementStartDate) {
            newErrors.agreementStartDate = "Start Date is required";
        }
        if (!formState.agreementEndDate) {
            newErrors.agreementEndDate = "End Date is required";
        }
        if (
            formState.agreementStartDate &&
            formState.agreementEndDate &&
            formState.agreementEndDate < formState.agreementStartDate
        ) {
            newErrors.agreementEndDate = "End Date must be after Start Date";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    useEffect(() => {
        fetchCustomers();
        fetchOrganizations();
        fetchEmployees();
    }, []);

    const fetchCustomers = async () => {
        setIsLoder(true);
        try {
            const response = await getlist(`${config.baseurl}Customer/getlist`);
            if (response?.status === 200) {
                setCustomers(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching customers:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const fetchOrganizations = async () => {
        try {
            const response = await getlist(`${config.baseurl}Organizations/getlist`);
            if (response?.status === 200) {
                setOrganizations(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching organizations:", error);
        }
    };

    const fetchEmployees = async () => {
        setIsLoder(true);
        try {
            const response = await getlist(`${config.baseurl}Employee/getlist`);
            if (response?.status === 200) {
                setEmployees(response.data || []);
            }
        } catch (error) {
            console.error("Error fetching employees:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const employeeOptions = employees.map((employee) => {
        const id = employee.id ?? employee.Id ?? employee.ID ?? 0;
        const fullName = employee.fullName ?? employee.FullName ?? "";
        const employeeCode = employee.employeeCode ?? employee.EmployeeCode ?? "";
        const employeeNumber = employee.employeeNumber ?? employee.EmployeeNumber ?? "";
        const workEmail = employee.workEmail ?? employee.WorkEmail ?? "";
        const userId = employee.userID ?? employee.userId ?? employee.UserID ?? employee.UserId ?? "";

        return {
            value: id,
            label: `${fullName}${employeeCode ? ` (${employeeCode})` : employeeNumber ? ` (${employeeNumber})` : ""}`,
            fullName,
            employeeCode,
            employeeNumber,
            workEmail,
            userId
        };
    });

    const selectedEmployeeOption =
        employeeOptions.find((option) => option.value === Number(formState.empId || 0)) || null;

    const employeeFilterOption = (option, rawInput) => {
        const query = (rawInput || "").trim().toLowerCase();
        if (!query) return true;

        const data = option.data || {};
        return (
            (option.label || "").toLowerCase().includes(query) ||
            (data.fullName || "").toLowerCase().includes(query) ||
            (data.employeeCode || "").toLowerCase().includes(query) ||
            (data.employeeNumber || "").toLowerCase().includes(query) ||
            (data.workEmail || "").toLowerCase().includes(query)
        );
    };

    const selectStyles = {
        control: (base, state) => ({
            ...base,
            minHeight: "46px",
            borderRadius: "0.75rem",
            borderColor: state.isFocused ? "#C04C2A" : "#D1D5DB",
            boxShadow: state.isFocused ? "0 0 0 2px rgba(192,76,42,0.15)" : "none",
            "&:hover": {
                borderColor: "#C04C2A"
            }
        }),
        menu: (base) => ({
            ...base,
            zIndex: 30
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        })
    };

    const openAddModal = () => {
        setFormState({
            id: 0,
            name: "",
            customerCode: "",
            address: "",
            email: "",
            mobile: "",
            whatsApp: "",
            domain: "",
            agreementStartDate: getDefaultStartDate(),
            agreementEndDate: getDefaultEndDate(),
            isCurrentCustomer: true,
            description: "",
            organizationId: organizations.length > 0 ? organizations[0].id : 0,
            empId: 0,
            empUserId: ""
        });
        setErrors({});
        setIsOpen(true);
    };

    const openEditModal = (item) => {
        setFormState({
            ...item,
            empId: item.empId ?? item.EmpId ?? 0,
            empUserId: item.empUserId ?? item.EmpUserId ?? "",
            agreementStartDate: item.agreementStartDate ? item.agreementStartDate.split('T')[0] : "",
            agreementEndDate: item.agreementEndDate ? item.agreementEndDate.split('T')[0] : ""
        });
        setErrors({});
        setIsOpen(true);
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsLoder(true);

        const payload = {
            ...formState,
            id: formState.id || 0,
            organizationId: Number(formState.organizationId),
            empId: Number(formState.empId || 0),
            empUserId: formState.empUserId || ""
        };

        try {
            const url = payload.id > 0
                ? `${config.baseurl}Customer/update`
                : `${config.baseurl}Customer/add`;

            const response = await post(url, payload);

            if (response?.status === 200) {
                setIsOpen(false);
                setErrors({});
                fetchCustomers();
            } else {
                console.error("Save failed:", response);
            }
        } catch (error) {
            console.error("API Error:", error);
        } finally {
            setIsLoder(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this customer?")) return;
        try {
            await deleteitem(`${config.baseurl}Customer/delete`, id);
            fetchCustomers();
        } catch (error) {
            console.error("Delete failed:", error);
        }
    };

    const filteredData = customers.filter(item =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.customerCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.mobile?.includes(searchTerm)
    );

    const paginatedData = filteredData.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-[#0F111A] min-h-screen transition-colors duration-300">
            <Breadcrumb pageName="Customers" currentPage="Customers" />

            <div className="bg-white dark:bg-[#161B22] rounded-xl shadow-sm border dark:border-gray-800 mt-4 overflow-hidden">
                {/* HEADER */}
                <div className="card-header flex flex-col sm:flex-row justify-between items-center px-5 py-3 border-b gap-4 transition-colors duration-300
                    bg-gradient-to-r from-[#C04C2A] to-[#4A1B13]
                    dark:from-[#2D120D] dark:to-[#1A1A1A] dark:border-gray-800">

                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">
                            Customer List
                        </h2>
                        {isLoder && <DotLoader color="#ffffff" size="w-1.5 h-1.5" />}
                    </div>

                    <div className="flex items-center gap-3 w-full sm:w-auto">
                        {/* Search Bar */}
                        <div className="relative flex-1 sm:w-64">
                            <input
                                type="text"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 rounded-full text-sm border-none focus:ring-2 focus:ring-white/50 bg-white/20 text-white placeholder-white/70 outline-none transition-all"
                            />
                            <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/70" />
                        </div>

                        <button
                            onClick={openAddModal}
                            className="flex items-center justify-center w-10 h-10 rounded-full transition-all
                            transform hover:scale-110 active:scale-95 shadow-lg flex-shrink-0
                            bg-white dark:bg-gray-200 text-[#C04C2A] dark:text-[#4A1B13]"
                        >
                            <FontAwesomeIcon icon={faPlus} className="text-lg" />
                        </button>
                    </div>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    <table className="w-full text-[12px] sm:text-sm min-w-[320px] sm:min-w-full">
                        <thead className="bg-gray-100 dark:bg-gray-800/50 uppercase text-[10px] tracking-wider text-gray-500 dark:text-gray-400">
                            <tr>
                                <th className="p-3 text-left">Name</th>
                                <th className="p-3 text-left hidden md:table-cell">Code</th>
                                <th className="p-3 text-left hidden lg:table-cell">Email</th>
                                <th className="p-3 text-left">Mobile</th>
                                <th className="p-3 text-center">Status</th>
                                <th className="p-3 text-right">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {paginatedData.length > 0 ? (
                                paginatedData.map(item => (
                                    <tr key={item.id} className="border-t dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-[#161B22]">
                                        <td className="p-3">
                                            <div className="font-bold text-gray-800 dark:text-gray-200 line-clamp-1">{item.name}</div>
                                            <div className="text-[10px] text-gray-400 dark:text-gray-500 line-clamp-1">{item.organizationName}</div>
                                        </td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400 font-mono text-xs hidden md:table-cell">{item.customerCode}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400 hidden lg:table-cell truncate max-w-[150px]">{item.email}</td>
                                        <td className="p-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">{item.mobile}</td>
                                        <td className="p-3 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${item.isCurrentCustomer
                                                ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800/50"
                                                : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700"
                                                }`}>
                                                {item.isCurrentCustomer ? "Active" : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="p-3 text-right">
                                            <div className="flex justify-end gap-2 sm:gap-3">
                                                <button
                                                    onClick={() => openEditModal(item)}
                                                    className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                                                    title="Edit"
                                                >
                                                    <FontAwesomeIcon icon={faEdit} />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 transition-colors"
                                                    title="Delete"
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="p-12 text-center text-gray-500">
                                        <div className="flex flex-col items-center gap-2">
                                            <FontAwesomeIcon icon={faSearch} size="2x" className="text-gray-300" />
                                            <p>No customers found.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINATION */}
                <div className="p-4 border-t dark:border-gray-800 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50 dark:bg-gray-800/20">
                    <span className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 font-medium">
                        Showing {paginatedData.length} of {filteredData.length} results
                    </span>

                    {filteredData.length > PAGE_SIZE && (
                        <div className="flex items-center gap-1 sm:gap-2">
                            <button
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page === 1}
                                className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs border dark:border-gray-700 rounded-md disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-300 dark:disabled:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-bold uppercase tracking-tighter dark:text-gray-300"
                            >
                                Prev
                            </button>
                            <span className="text-[10px] sm:text-xs font-bold px-1 sm:px-2 bg-gray-50 dark:bg-gray-800 py-1 rounded border dark:border-gray-700 dark:text-gray-300">Page {page} of {Math.ceil(filteredData.length / PAGE_SIZE)}</span>
                            <button
                                onClick={() => setPage(p => Math.min(Math.ceil(filteredData.length / PAGE_SIZE), p + 1))}
                                disabled={page >= Math.ceil(filteredData.length / PAGE_SIZE)}
                                className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs border dark:border-gray-700 rounded-md disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-300 dark:disabled:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-bold uppercase tracking-tighter dark:text-gray-300"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* MODAL */}
            {isOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setIsOpen(false)}
                    ></div>

                    <div className="bg-white dark:bg-gray-900 rounded-2xl w-full max-w-3xl p-6 sm:p-8 relative shadow-2xl
                     transform transition-all animate-in fade-in zoom-in duration-200 max-h-[95vh] overflow-y-auto">

                        <button
                            type="button"
                            onClick={() => setIsOpen(false)}
                            className="absolute top-4 right-4 text-gray-400 hover:text-red-500 transition-colors z-10"
                        >
                            <FontAwesomeIcon icon={faTimes} size="lg" />
                        </button>

                        <h2 className="text-2xl font-bold mb-6 text-gray-800 dark:text-white flex items-center gap-2">
                            <div className="w-2 h-8 bg-[#C04C2A] rounded-full"></div>
                            {formState.id ? "Update Customer" : "Create New Customer"}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                            {/* Basic Info */}
                            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                            ${errors.name ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                        value={formState.name}
                                        placeholder="John Doe"
                                        onChange={e => {
                                            setFormState({ ...formState, name: e.target.value });
                                            if (errors.name) setErrors({ ...errors, name: "" });
                                        }}
                                    />
                                    {errors.name && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.name}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Customer Code <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                            ${errors.customerCode ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                        value={formState.customerCode}
                                        placeholder="CUST-001"
                                        onChange={e => {
                                            setFormState({ ...formState, customerCode: e.target.value });
                                            if (errors.customerCode) setErrors({ ...errors, customerCode: "" });
                                        }}
                                    />
                                    {errors.customerCode && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.customerCode}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Organization <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                            ${errors.organizationId ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                        value={formState.organizationId}
                                        onChange={e => {
                                            setFormState({ ...formState, organizationId: e.target.value });
                                            if (errors.organizationId) setErrors({ ...errors, organizationId: "" });
                                        }}
                                    >
                                        <option value="0">Select Organization</option>
                                        {organizations.map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                    {errors.organizationId && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.organizationId}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Employee
                                    </label>
                                    <Select
                                        value={selectedEmployeeOption}
                                        onChange={(option) =>
                                            setFormState({
                                                ...formState,
                                                empId: option?.value || 0,
                                                empUserId: option?.userId || ""
                                            })
                                        }
                                        options={employeeOptions}
                                        isSearchable
                                        isClearable
                                        placeholder="Search employee"
                                        className="w-full"
                                        filterOption={employeeFilterOption}
                                        menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                                        styles={selectStyles}
                                    />
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                            ${errors.email ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                        value={formState.email}
                                        placeholder="example@mail.com"
                                        onChange={e => {
                                            setFormState({ ...formState, email: e.target.value });
                                            if (errors.email) setErrors({ ...errors, email: "" });
                                        }}
                                    />
                                    {errors.email && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.email}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Mobile <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2
                                            ${errors.mobile ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                        value={formState.mobile}
                                        placeholder="+880 1XXX-XXXXXX"
                                        onChange={e => {
                                            setFormState({ ...formState, mobile: e.target.value });
                                            if (errors.mobile) setErrors({ ...errors, mobile: "" });
                                        }}
                                    />
                                    {errors.mobile && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.mobile}</p>}
                                </div>

                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        WhatsApp
                                    </label>
                                    <input
                                        className="w-full border border-gray-300 dark:border-gray-700 px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-orange-100 focus:border-[#C04C2A]"
                                        value={formState.whatsApp || ""}
                                        placeholder="+880 1XXX-XXXXXX"
                                        onChange={e => setFormState({ ...formState, whatsApp: e.target.value })}
                                    />
                                </div>
                            </div>

                            {/* More Details */}
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                        Domain / Website
                                    </label>
                                    <input
                                        className="w-full border border-gray-300 dark:border-gray-700 px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-orange-100 focus:border-[#C04C2A]"
                                        value={formState.domain || ""}
                                        placeholder="www.example.com"
                                        onChange={e => setFormState({ ...formState, domain: e.target.value })}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                            Start Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 text-xs
                                                ${errors.agreementStartDate ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                            value={formState.agreementStartDate || ""}
                                            onChange={e => {
                                                setFormState({ ...formState, agreementStartDate: e.target.value });
                                                if (errors.agreementStartDate) setErrors({ ...errors, agreementStartDate: "" });
                                            }}
                                        />
                                        {errors.agreementStartDate && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.agreementStartDate}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                            End Date <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className={`w-full border px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 text-xs
                                                ${errors.agreementEndDate ? "border-red-500 ring-red-100" : "border-gray-300 dark:border-gray-700 focus:ring-orange-100 focus:border-[#C04C2A]"}`}
                                            value={formState.agreementEndDate || ""}
                                            onChange={e => {
                                                setFormState({ ...formState, agreementEndDate: e.target.value });
                                                if (errors.agreementEndDate) setErrors({ ...errors, agreementEndDate: "" });
                                            }}
                                        />
                                        {errors.agreementEndDate && <p className="mt-1 text-[10px] text-red-500 font-bold uppercase">{errors.agreementEndDate}</p>}
                                    </div>
                                </div>

                                <div className="flex items-center gap-3 py-2">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="sr-only peer"
                                            checked={formState.isCurrentCustomer}
                                            onChange={e => setFormState({ ...formState, isCurrentCustomer: e.target.checked })}
                                        />
                                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#C04C2A]"></div>
                                        <span className="ml-3 text-sm font-bold text-gray-700 dark:text-gray-300">Is Current Customer</span>
                                    </label>
                                </div>
                            </div>

                            {/* Address & Description */}
                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                    Address
                                </label>
                                <textarea
                                    className="w-full border border-gray-300 dark:border-gray-700 px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-orange-100 focus:border-[#C04C2A] h-20 resize-none transition-all"
                                    value={formState.address || ""}
                                    placeholder="Enter full address..."
                                    onChange={e => setFormState({ ...formState, address: e.target.value })}
                                />
                            </div>

                            <div className="md:col-span-2">
                                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                                    Description
                                </label>
                                <textarea
                                    className="w-full border border-gray-300 dark:border-gray-700 px-4 py-2.5 rounded-xl dark:bg-gray-800 dark:text-white outline-none focus:ring-2 focus:ring-orange-100 focus:border-[#C04C2A] h-20 resize-none transition-all"
                                    value={formState.description || ""}
                                    placeholder="Add any additional notes..."
                                    onChange={e => setFormState({ ...formState, description: e.target.value })}
                                />
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-4 pt-8">
                            <button
                                type="button"
                                onClick={() => setIsOpen(false)}
                                className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-700
                                    text-gray-600 dark:text-gray-300 font-bold rounded-xl
                                    hover:bg-gray-100 dark:hover:bg-gray-800 transition-all font-mono uppercase text-xs tracking-widest"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={isLoder}
                                className="flex-[2] px-4 py-3 text-white font-bold rounded-xl
                                    shadow-lg transform transition-all active:scale-95 disabled:opacity-70 
                                    font-mono uppercase text-xs tracking-widest flex items-center justify-center"
                                style={{
                                    background: "linear-gradient(to right, #C04C2A, #4A1B13)"
                                }}
                            >
                                {isLoder ? (
                                    <DotLoader color="#fff" size="w-1.5 h-1.5" />
                                ) : (
                                    formState.id ? "Update Customer" : "Save Customer"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
